import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ALL_EXAMPLE_NOVEL_IDS,
  EXAMPLE_NOVEL_FALLBACK_LANG,
  EXAMPLE_NOVEL_ID_BY_LANG,
  EXAMPLE_NOVEL_OWNER_ID,
} from '@/config/example-novel-config';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { updateDocumentNonBlocking } from '@/firebase/non-blocking-updates';
import type { CollaboratedNovel } from '@/lib/types';

export interface ExampleNovelState {
  showExampleNovel: boolean;
  exampleNovelEntry: CollaboratedNovel | null;
  novelId: string;
  /** Call to silently set displayExampleNovel: false on the user doc. */
  dismissExampleNovel: () => void;
}

/**
 * Resolves the per-language example novel for the current user.
 *
 * - Picks the correct novel ID from `EXAMPLE_NOVEL_ID_BY_LANG` based on
 *   the active i18next language (region codes stripped: 'en-US' → 'en').
 * - Falls back to 'en' when the language is not in the map.
 * - OPT-IN: example novels are hidden by default. They only appear when
 *   `displayExampleNovel === true` on the user profile (admin-controlled rollout).
 * - The ePlotera owner account itself never sees the injected entry.
 */
export function useExampleNovel(): ExampleNovelState {
  const { i18n, t } = useTranslation();
  const { userData, docRef } = useUserProfile();
  const { user } = useFirebase();

  const novelId = useMemo(() => {
    const lang = (i18n.language ?? EXAMPLE_NOVEL_FALLBACK_LANG).split('-')[0];
    return (
      EXAMPLE_NOVEL_ID_BY_LANG[lang] ??
      EXAMPLE_NOVEL_ID_BY_LANG[EXAMPLE_NOVEL_FALLBACK_LANG]
    );
  }, [i18n.language]);

  // Opt-in: only show when the flag is explicitly true.
  // Absent (undefined) or false → hidden. Owner account is always excluded.
  const isOwnerAccount = user?.uid === EXAMPLE_NOVEL_OWNER_ID;
  const showExampleNovel = !isOwnerAccount && userData?.displayExampleNovel === true;

  const exampleNovelEntry = useMemo<CollaboratedNovel | null>(() => {
    if (!showExampleNovel) return null;
    return {
      ownerId: EXAMPLE_NOVEL_OWNER_ID,
      novelId,
      role: 'example',
      novelTitle: t('example_novel.title'),
    };
  }, [showExampleNovel, novelId, t]);

  const dismissExampleNovel = () => {
    if (docRef) {
      updateDocumentNonBlocking(docRef, { displayExampleNovel: false });
    }
  };

  // Keep ALL_EXAMPLE_NOVEL_IDS referenced so the import is not flagged as unused.
  void ALL_EXAMPLE_NOVEL_IDS;

  return { showExampleNovel, exampleNovelEntry, novelId, dismissExampleNovel };
}
